import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Calendar } from './ui/calendar';
import { bookConsultation, getAvailableSlots } from '../services/api';
import { trackSchedule } from '../utils/metaPixel';
import { trackGaEvent } from '../utils/ga';
import { hu } from 'date-fns/locale';
import { Calendar as CalendarIcon, Clock, CheckCircle2, MapPin, Sparkles, CreditCard, Banknote, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { GYM_VENUE } from '../config/venue';
import { getUtm } from '../utils/utm';
import { usePaidAssessment } from '../utils/usePublicConfig';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const PROGRAM_GOALS = [
  'Irodai Életmódváltó',
  'Izomépítés és formázás',
  '1:1 Online Mentorálás',
];

const FITNESS_LEVELS = [
  'Teljesen kezdő',
  'Középhaladó',
  'Haladó testépítő',
];

const LIFESTYLES = [
  'Irodai ülőmunka',
  'Folyamatos váltóműszak',
  'Egyéb',
];

const initialForm = {
  name: '',
  email: '',
  phone: '',
  program_goal: '',
  focus_area: '',
  obstacle: '',
  fitness_level: '',
  lifestyle: '',
};

// Magyar mobilszám ellenőrzése + normalizálása (a backend validátor tükre).
const HU_MOBILE_PREFIXES = ['20', '30', '31', '50', '70'];
export const normalizeHuMobile = (raw) => {
  let d = (raw || '').replace(/\D/g, '');
  if (d.startsWith('0036')) d = d.slice(4);
  else if (d.startsWith('36') && d.length === 11) d = d.slice(2);
  else if (d.startsWith('06')) d = d.slice(2);
  else if (d.startsWith('0') && d.length === 10) d = d.slice(1);
  if (d.length !== 9 || !HU_MOBILE_PREFIXES.includes(d.slice(0, 2))) return '';
  return `+36 ${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5)}`;
};

const BookingModal = ({ isOpen, onClose, defaultDuration = 20 }) => {
  const paidAssessment = usePaidAssessment();
  const [phoneError, setPhoneError] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState(null);
  // 20 = ingyenes 20 perces konzultáció (default — this modal was originally
  // wired only for the free chat CTA); 60 = teljes edzés / állapotfelmérés.
  const [duration, setDuration] = useState(defaultDuration);
  // Hogyan tartsuk a 20 perces ingyenes konzultációt: személyesen vagy online.
  const [meetingMode, setMeetingMode] = useState('');          // 'in_person' | 'online'
  const [onlinePlatform, setOnlinePlatform] = useState('');    // google_meet | messenger
  const [onlineContact, setOnlineContact] = useState('');      // FB-név/link vagy e-mail
  const [bookingStep, setBookingStep] = useState('select');
  const [formData, setFormData] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  // Promo hint — visible only if the visitor arrived via the promo popup CTA
  // AND there's still an active promo. The hint sits under the 60-min option
  // to direct them to book the full appointment (which is what the discount
  // requires) and reassure them they can pay in person at the consultation.
  const [activePromo, setActivePromo] = useState(null);
  const [cameFromPromoCta, setCameFromPromoCta] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    try { setCameFromPromoCta(!!sessionStorage.getItem('oc_from_promo_cta')); } catch { /* ignore */ }
    axios.get(`${API_BASE}/api/promo-popup/active`, { timeout: 5000 })
      .then(({ data }) => setActivePromo(data?.promo || null))
      .catch(() => setActivePromo(null));
  }, [isOpen]);
  // Clear the promo-arrival flag once the modal closes so the hint doesn't
  // linger after the visitor is done (or intentionally closed without booking).
  useEffect(() => {
    if (isOpen) return;
    try { sessionStorage.removeItem('oc_from_promo_cta'); } catch { /* ignore */ }
  }, [isOpen]);
  // Show the discount hint whenever a promo is live, regardless of where the
  // visitor opened the booking modal from. `cameFromPromoCta` is kept only for
  // analytics/auto-select nuance.
  // A kedvezmény a 60 perces időponthoz kötődik, ezért a 20 perces
  // ingyenes konzultációnál nem jelenik meg a hint.
  const showPromoHint = !!activePromo && duration === 60;
  // Auto-select the 60-min option when arriving from a promo CTA — that's
  // the appointment the discount is tied to, so it should be the default.
  useEffect(() => {
    if (cameFromPromoCta && activePromo) setDuration(60);
  }, [cameFromPromoCta, activePromo]);
  const [, setIsLoadingSlots] = useState(false);

  // Google Meet esetén alapértelmezetten a megadott e-mail cím az elérhetőség.
  useEffect(() => {
    if (onlinePlatform === 'google_meet' && !onlineContact && formData.email) {
      setOnlineContact(formData.email);
    }
  }, [formData.email, onlinePlatform, onlineContact]);

  useEffect(() => {
    if (isOpen) {
      fetchAvailableSlots();
      // GA4 — funnel 1. lépés: megnyílt a foglalási űrlap (CTA kattintás).
      trackGaEvent('booking_open', {
        page_path: window.location.pathname,
        duration_minutes: duration,
      });
    }
  }, [isOpen]);

  const fetchAvailableSlots = async () => {
    setIsLoadingSlots(true);
    try {
      const response = await getAvailableSlots();
      const slots = response.slots || [];
      setAvailableSlots(slots);
      // A backend már kiszűrte a múltbeli és a 12 órán belüli időpontokat,
      // így az első foglalható napra ugrunk (ne egy üres napot lássanak).
      const firstDate = slots.map((s) => s.date).sort()[0];
      if (firstDate) {
        const [y, m, d] = firstDate.split('-').map(Number);
        setSelectedDate(new Date(y, m - 1, d));
        setSelectedSlot(null);
      }
    } catch (error) {
      console.error('Időpontok betöltése sikertelen:', error);
      toast.error('Az elérhető időpontok betöltése sikertelen');
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handleDateSelect = (date) => {
    // react-day-picker (mode="single") passes undefined when the already
    // selected day is clicked again → would crash formatDateString().
    if (!date) return;
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  const handleSlotSelect = (slot) => {
    if (slot.available) {
      setSelectedSlot(slot);
    }
  };

  const handleField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'phone' && phoneError && normalizeHuMobile(value)) setPhoneError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Hungarian mobile only — mirrors the backend validator.
    const normalizedPhone = normalizeHuMobile(formData.phone);
    if (!normalizedPhone) {
      setPhoneError('Kérlek adj meg egy érvényes magyar mobilszámot (pl. +36 70 123 4567).');
      toast.error('Érvénytelen telefonszám.');
      return;
    }
    setPhoneError('');

    // Final guard: only the program goal is mandatory among the selects.
    if (!formData.program_goal) {
      toast.error('Kérlek válaszd ki, melyik program érdekel.');
      return;
    }

    setIsSubmitting(true);
    const isOnline = duration === 20 && meetingMode === 'online';
    const contact = isOnline
      ? (onlineContact || (onlinePlatform === 'google_meet' ? formData.email : '')).trim()
      : null;
    if (isOnline && contact.length < 3) {
      setIsSubmitting(false);
      toast.error(onlinePlatform === 'messenger'
        ? 'Add meg a Facebook-neved vagy profillinked.'
        : 'Add meg az e-mail címed a Google Meet linkhez.');
      return;
    }
    const utm = getUtm();
    try {
      const result = await bookConsultation({
        name: formData.name,
        email: formData.email,
        phone: normalizedPhone,
        program_goal: formData.program_goal,
        focus_area: formData.focus_area || null,
        obstacle: formData.obstacle || null,
        fitness_level: formData.fitness_level || null,
        lifestyle: formData.lifestyle || null,
        commitment: null,
        meeting_mode: duration === 20 ? meetingMode : 'in_person',
        online_platform: isOnline ? onlinePlatform : null,
        online_contact: isOnline ? contact : null,
        source_page: window.location.pathname,
        utm_source: utm.utm_source || null,
        utm_medium: utm.utm_medium || null,
        utm_campaign: utm.utm_campaign || null,
        utm_content: utm.utm_content || null,
        slot: {
          date: selectedSlot.date,
          time: selectedSlot.time,
          duration_minutes: duration,
        },
      });

      if (result.success) {
        setBookingStep('success');
        toast.success('Küldtünk egy megerősítő e-mailt!');
        trackSchedule({ content_name: duration === 20 ? 'consultation_free_20min' : 'training_60min' });
        // GA4 — funnel 3. lépés: sikeres foglalás.
        trackGaEvent('booking_complete', {
          page_path: window.location.pathname,
          duration_minutes: duration,
          meeting_mode: duration === 20 ? meetingMode : 'in_person',
          online_platform: isOnline ? onlinePlatform : 'none',
          utm_source: utm.utm_source || 'direct',
          utm_campaign: utm.utm_campaign || 'none',
          utm_content: utm.utm_content || 'none',
        });
      }
    } catch (error) {
      const msg = error?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'A foglalás nem sikerült. Kérlek próbáld újra.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setBookingStep('select');
    setSelectedSlot(null);
    setMeetingMode('');
    setOnlinePlatform('');
    setOnlineContact('');
    setFormData(initialForm);
    onClose();
  };

  // Format date to YYYY-MM-DD for matching slot.date (avoids timezone issues)
  const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const selectedDateStr = formatDateString(selectedDate);
  // Az első foglalható nap (a backend már a 12 órás minimumot is figyelembe
  // vette) — az ez előtti napok a naptárban nem választhatók.
  const firstBookableDateStr = availableSlots.map((s) => s.date).sort()[0] || null;
  const isDayDisabled = (date) => {
    const todayStart = new Date(new Date().setHours(0, 0, 0, 0));
    if (date < todayStart) return true;
    if (firstBookableDateStr && formatDateString(date) < firstBookableDateStr) return true;
    return false;
  };
  const hourlySlots = availableSlots
    .filter((slot) => slot.date === selectedDateStr)
    .sort((a, b) => a.time.localeCompare(b.time));

  // For 20-min bookings, each AVAILABLE hourly slot expands into 3 sub-slots
  // at :00, :20 and :40. The parent slot's `id` is preserved so booking the
  // sub-slot on the backend still resolves to the parent hour and blocks it.
  const filteredSlots = duration === 20
    ? hourlySlots.flatMap((slot) => {
        if (!slot.available) {
          return [{ ...slot, subKey: `${slot.id}-blocked`, displayTime: slot.time }];
        }
        const hourPrefix = slot.time.split(':')[0];
        return ['00', '20', '40'].map((mm) => ({
          ...slot,
          time: `${hourPrefix}:${mm}`,
          displayTime: `${hourPrefix}:${mm}`,
          subKey: `${slot.id}-${mm}`,
        }));
      })
    : hourlySlots.map((slot) => ({ ...slot, subKey: slot.id, displayTime: slot.time }));

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent data-testid="booking-modal" className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {bookingStep === 'success' ? 'Már csak egy kattintás!' : 'Ingyenes konzultáció foglalása'}
          </DialogTitle>
          <DialogDescription>
            {bookingStep === 'success'
              ? 'Erősítsd meg a foglalásodat az e-mailben kapott gombbal.'
              : 'Válassz egy számodra megfelelő dátumot és időpontot.'}
          </DialogDescription>
        </DialogHeader>

        {bookingStep === 'success' ? (
          <div className="py-8 text-center" data-testid="booking-success">
            <CheckCircle2 className="mx-auto text-green-500 mb-4" size={64} />
            <h3 className="text-xl font-bold mb-2">Nézd meg az e-mail fiókodat!</h3>
            <p className="text-gray-600 mb-2">
              A kiválasztott időpont: {selectedSlot?.date}, {selectedSlot?.time}
            </p>
            <p className="text-sm text-gray-600 mb-2">
              Küldtünk egy e-mailt a(z) <span className="font-semibold">{formData.email}</span> címre —
              kattints benne a <span className="font-semibold">„Foglalás megerősítése"</span> gombra, és
              az időpont végleges lesz.
            </p>
            <p className="text-xs text-gray-500 mb-6">
              Az időpontot 2 órán keresztül tartjuk fenn neked. Ha addig nem erősíted meg, felszabadul,
              hogy más is lefoglalhassa. (Nézd meg a Spam / Promóciók mappát is.)
            </p>
            <Button
              data-testid="booking-close-btn"
              onClick={resetAndClose}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              Bezárás
            </Button>
          </div>
        ) : (
          <Tabs value={bookingStep} onValueChange={setBookingStep} className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="select" disabled={bookingStep === 'details' && !selectedSlot}>
                1. Időpont
              </TabsTrigger>
              <TabsTrigger value="details" disabled={!selectedSlot}>
                2. Adataid
              </TabsTrigger>
            </TabsList>

            <TabsContent value="select" className="space-y-6 mt-6">
              {/* Duration selector — 20 perc ingyenes vs 60 perc edzés. Reset
                  the currently-picked slot whenever the mode flips so the
                  frontend never sends an outdated (hour, offset) combo. */}
              <div
                data-testid="booking-duration-toggle"
                className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-lg"
              >
                {[
                  { v: 20, label: '20 perces ingyenes konzultáció', sub: 'Rövid ismerkedő beszélgetés' },
                  { v: 60, label: '60 perc konzultáció / ingyenes állapotfelmérés', sub: 'Teljes egy órás időpont · díjmentes' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`booking-duration-${opt.v}`}
                    onClick={() => { setDuration(opt.v); setSelectedSlot(null); }}
                    className={`p-3 rounded-md text-left transition-all ${
                      duration === opt.v
                        ? 'bg-white shadow-sm border border-yellow-500'
                        : 'text-gray-600 hover:bg-white/60'
                    }`}
                  >
                    <div className="text-sm font-bold text-gray-900">{opt.label}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">{opt.sub}</div>
                  </button>
                ))}
              </div>

              {/* Promo-arrival hint — visible only when the visitor clicked the
                  promo popup CTA AND the promo is still live. Explains that the
                  60-min slot is the one the discount is attached to. */}
              {showPromoHint && (
                <div
                  data-testid="booking-promo-hint"
                  className="flex gap-3 p-4 bg-gradient-to-br from-yellow-50 to-amber-50 border-l-4 border-yellow-500 rounded-lg text-sm text-gray-800 leading-relaxed shadow-sm"
                >
                  <Sparkles className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
                  <div className="space-y-1.5">
                    <div className="font-bold text-gray-900">
                      A kedvezményhez a 60 perces konzultáció / ingyenes állapotfelmérés időpontját foglald!
                    </div>
                    <p className="text-[13px] text-gray-700">
                      A konzultáción mindent megbeszélünk (célok, program, ütemezés), és <strong>ott a helyszínen fizethetsz</strong> — {' '}
                      <span className="inline-flex items-center gap-1"><Banknote size={13} className="text-emerald-700" /> készpénzzel</span>
                      {' vagy '}
                      <span className="inline-flex items-center gap-1"><CreditCard size={13} className="text-emerald-700" /> bankkártyával</span>. Nem kell semmit előre utalni.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        // Close the modal and open the site-wide chat widget
                        // so the visitor can ask a question without losing the
                        // promo context.
                        onClose && onClose();
                        setTimeout(() => {
                          try { window.dispatchEvent(new Event('oc:open-chat')); } catch { /* ignore */ }
                        }, 220);
                      }}
                      data-testid="booking-promo-hint-chat-link"
                      className="inline-flex items-center gap-1 text-[13px] font-bold text-yellow-800 hover:text-yellow-900 border-b border-yellow-700/40 hover:border-yellow-800 pb-0.5 transition-colors"
                    >
                      <MessageCircle size={13} />
                      Kérdésed van? Írj nekem →
                    </button>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                {/* Calendar */}
                <div>
                  <Label className="mb-3 flex items-center gap-2">
                    <CalendarIcon size={18} />
                    Dátum kiválasztása
                  </Label>
                  <Calendar
                    mode="single"
                    locale={hu}
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={isDayDisabled}
                    className="rounded-md border"
                  />
                </div>

                {/* Time Slots */}
                <div>
                  <Label className="mb-3 flex items-center gap-2">
                    <Clock size={18} />
                    Elérhető időpontok
                  </Label>
                  <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="booking-slots-list">
                    {filteredSlots.length > 0 ? (
                      filteredSlots.map((slot) => (
                        <button
                          key={slot.subKey}
                          data-testid={`booking-slot-${slot.time.replace(/[:\s]/g, '')}`}
                          onClick={() => handleSlotSelect(slot)}
                          disabled={!slot.available}
                          className={`w-full p-3 rounded-lg border-2 text-left transition-all duration-200 ${
                            selectedSlot?.subKey === slot.subKey
                              ? 'border-yellow-600 bg-yellow-50'
                              : slot.available
                              ? 'border-gray-200 hover:border-yellow-400'
                              : 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                          }`}
                        >
                          <div className="font-semibold">
                            {slot.displayTime}
                            <span className="ml-2 text-xs font-normal text-gray-500">
                              · {duration} perc
                            </span>
                          </div>
                          <div className="text-sm text-gray-500">
                            {slot.available ? 'Elérhető' : 'Foglalt'}
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        Nincs elérhető időpont ezen a napon
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Konzultáció módja — a 20 perces ingyenes konzultációnál kötelező */}
              {duration === 20 && selectedSlot && (
                <div data-testid="booking-mode-block" className="mb-5">
                  <div className="text-sm font-bold text-gray-900 mb-2">
                    Hogyan szeretnéd a konzultációt? <span className="text-red-500">*</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {[
                      { v: 'in_person', t: 'Személyesen a BB Gymben', s: 'Miskolc, Szeles u. 71.' },
                      { v: 'online', t: 'Online videóhívásban', s: 'Otthonról, bárhonnan' },
                    ].map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        data-testid={`booking-mode-${o.v}`}
                        onClick={() => { setMeetingMode(o.v); if (o.v === 'in_person') setOnlinePlatform(''); }}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          meetingMode === o.v
                            ? 'border-yellow-500 bg-yellow-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="font-semibold text-sm text-gray-900">{o.t}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{o.s}</div>
                      </button>
                    ))}
                  </div>

                  {meetingMode === 'online' && (
                    <div className="mt-3" data-testid="booking-platform-block">
                      <div className="text-sm font-bold text-gray-900 mb-2">
                        Melyik platform kényelmes neked? <span className="text-red-500">*</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { v: 'google_meet', t: 'Google Meet' },
                          { v: 'messenger', t: 'Messenger' },
                        ].map((p) => (
                          <button
                            key={p.v}
                            type="button"
                            data-testid={`booking-platform-${p.v}`}
                            onClick={() => {
                              setOnlinePlatform(p.v);
                              setOnlineContact(p.v === 'google_meet' ? (formData.email || '') : '');
                            }}
                            className={`px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all ${
                              onlinePlatform === p.v
                                ? 'border-yellow-500 bg-yellow-50 text-gray-900'
                                : 'border-gray-200 hover:border-gray-300 bg-white text-gray-700'
                            }`}
                          >
                            {p.t}
                          </button>
                        ))}
                      </div>

                      {onlinePlatform && (
                        <div className="mt-3" data-testid="booking-online-contact-block">
                          <Label htmlFor="booking-online-contact" className="text-sm font-bold text-gray-900">
                            {onlinePlatform === 'messenger'
                              ? 'Facebook-neved vagy profillinked'
                              : 'E-mail cím a Google Meet linkhez'} <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            data-testid="booking-online-contact-input"
                            id="booking-online-contact"
                            type={onlinePlatform === 'messenger' ? 'text' : 'email'}
                            value={onlineContact}
                            onChange={(e) => setOnlineContact(e.target.value)}
                            placeholder={onlinePlatform === 'messenger'
                              ? 'pl. Kovács János vagy facebook.com/kovacsjanos'
                              : 'janos@example.com'}
                            className="mt-1.5"
                          />
                          <p className="mt-1.5 text-xs text-gray-500">
                            {onlinePlatform === 'messenger'
                              ? 'Ezen a profilon kereslek a konzultáció előtt.'
                              : 'Erre a címre küldöm a videóhívás linkjét.'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-xs text-gray-500 leading-relaxed mt-3" data-testid="booking-mode-note">
                    Az online konzultáció ismerkedő beszélgetés. A személyes programoknál az
                    állapotfelmérés a BB Gymben történik.
                  </p>
                </div>
              )}

              <Button
                data-testid="booking-continue-btn"
                onClick={() => {
                  setBookingStep('details');
                  // GA4 — funnel 2. lépés: eljutott az „Adataid" képernyőig.
                  trackGaEvent('booking_step_details', {
                    page_path: window.location.pathname,
                    duration_minutes: duration,
                    meeting_mode: duration === 20 ? meetingMode : 'in_person',
                    online_platform: meetingMode === 'online' ? onlinePlatform : 'none',
                  });
                }}
                disabled={!selectedSlot || (duration === 20 && (
                  !meetingMode ||
                  (meetingMode === 'online' && (!onlinePlatform || (onlinePlatform === 'messenger' && onlineContact.trim().length < 3)))
                ))}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-semibold"
              >
                Tovább az adatokhoz
              </Button>
            </TabsContent>

            <TabsContent value="details" className="mt-6">
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="booking-details-form">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-yellow-800 mb-1">
                    <CalendarIcon size={18} />
                    <span className="font-semibold">Kiválasztott időpont</span>
                  </div>
                  <div className="text-gray-700">
                    {selectedDate.toLocaleDateString('hu-HU', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                    {', '}
                    {selectedSlot?.time}
                    <span className="ml-2 text-xs text-gray-500">
                      · {duration} perc {duration === 20 ? '(ingyenes konzultáció)' : '(konzultáció / ingyenes állapotfelmérés)'}
                    </span>
                  </div>
                  {/* Helyszín — online konzultációnál a platform, egyébként a terem. */}
                  <div
                    data-testid="booking-venue-label"
                    className="mt-3 pt-3 border-t border-yellow-200 flex items-start gap-2 text-xs text-gray-700"
                  >
                    <MapPin size={13} className="text-yellow-700 mt-0.5 flex-shrink-0" />
                    <div className="leading-relaxed">
                      {duration === 20 && meetingMode === 'online' ? (
                        <>
                          <span className="font-semibold text-gray-900">
                            Helyszín: Online videóhívás
                            {onlinePlatform === 'messenger' ? ' – Messenger' : ' – Google Meet'}
                          </span>
                          <br />
                          <span className="text-gray-600">
                            {onlinePlatform === 'messenger'
                              ? 'A megadott Facebook-profilodon kereslek.'
                              : 'A hívás linkjét e-mailben küldöm.'}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="font-semibold text-gray-900">Helyszín: {GYM_VENUE.name}</span>
                          <br />
                          <span className="text-gray-600">{GYM_VENUE.fullAddress}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact fields */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="booking-name">Teljes név *</Label>
                    <Input
                      data-testid="booking-name-input"
                      id="booking-name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => handleField('name', e.target.value)}
                      placeholder="Kovács János"
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="booking-email">E-mail cím *</Label>
                    <Input
                      data-testid="booking-email-input"
                      id="booking-email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleField('email', e.target.value)}
                      placeholder="janos@example.com"
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="booking-phone">Telefonszám *</Label>
                  <Input
                    data-testid="booking-phone-input"
                    id="booking-phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => handleField('phone', e.target.value)}
                    onBlur={(e) => setPhoneError(
                      !e.target.value || normalizeHuMobile(e.target.value)
                        ? ''
                        : 'Kérlek adj meg egy érvényes magyar mobilszámot (pl. +36 70 123 4567).'
                    )}
                    placeholder="+36 30 123 4567"
                    className={`mt-2 ${phoneError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                    aria-invalid={!!phoneError}
                  />
                  {phoneError ? (
                    <p data-testid="booking-phone-error" className="mt-1.5 text-xs text-red-600">
                      {phoneError}
                    </p>
                  ) : (
                    <p className="mt-1.5 text-xs text-gray-500">
                      Magyar mobilszám: +36 20 / 30 / 31 / 50 / 70 és 7 számjegy
                    </p>
                  )}
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <div className="text-sm font-bold text-gray-700 mb-1">
                    Ha szeretnéd, mondj el pár dolgot magadról (nem kötelező)
                  </div>
                  <p className="text-xs text-gray-500 mb-4">
                    Hogy a konzultáción a leghatékonyabban tudjak segíteni neked.
                  </p>
                </div>

                {/* Program goal */}
                <div>
                  <Label className="mb-2 block">Cél (melyik program érdekel leginkább) *</Label>
                  <Select
                    value={formData.program_goal}
                    onValueChange={(v) => handleField('program_goal', v)}
                  >
                    <SelectTrigger data-testid="booking-program-goal-select" className="mt-1">
                      <SelectValue placeholder="Válassz programot…" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROGRAM_GOALS.map((v) => (
                        <SelectItem
                          key={v}
                          value={v}
                          data-testid={`booking-program-goal-${v.replace(/\s+/g, '-')}`}
                        >
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Miben szeretne fejlődni */}
                <div>
                  <Label htmlFor="booking-focus" className="mb-2 block">
                    Melyik testrészen / miben szeretnél fejlődni?
                  </Label>
                  <Textarea
                    data-testid="booking-focus-input"
                    id="booking-focus"
                    name="focus_area"
                    rows={2}
                    value={formData.focus_area}
                    onChange={(e) => handleField('focus_area', e.target.value)}
                    placeholder="pl. láb, fenék, erő, állóképesség"
                    className="mt-1"
                  />
                </div>

                {/* Mi akadályozta eddig */}
                <div>
                  <Label htmlFor="booking-obstacle" className="mb-2 block">
                    Mi akadályozott eddig?
                  </Label>
                  <Textarea
                    data-testid="booking-obstacle-input"
                    id="booking-obstacle"
                    name="obstacle"
                    rows={2}
                    value={formData.obstacle}
                    onChange={(e) => handleField('obstacle', e.target.value)}
                    placeholder="pl. időhiány, motiváció, sérülés, nem tudtam hogyan kezdjem"
                    className="mt-1"
                  />
                </div>

                {/* Fitness level + Lifestyle */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 block">Edzettségi szint</Label>
                    <Select
                      value={formData.fitness_level}
                      onValueChange={(v) => handleField('fitness_level', v)}
                    >
                      <SelectTrigger data-testid="booking-fitness-level-select" className="mt-1">
                        <SelectValue placeholder="Válassz szintet…" />
                      </SelectTrigger>
                      <SelectContent>
                        {FITNESS_LEVELS.map((v) => (
                          <SelectItem
                            key={v}
                            value={v}
                            data-testid={`booking-fitness-${v.replace(/\s+/g, '-')}`}
                          >
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-2 block">Életmód</Label>
                    <Select
                      value={formData.lifestyle}
                      onValueChange={(v) => handleField('lifestyle', v)}
                    >
                      <SelectTrigger data-testid="booking-lifestyle-select" className="mt-1">
                        <SelectValue placeholder="Válassz életmódot…" />
                      </SelectTrigger>
                      <SelectContent>
                        {LIFESTYLES.map((v) => (
                          <SelectItem
                            key={v}
                            value={v}
                            data-testid={`booking-lifestyle-${v.replace(/\s+/g, '-')}`}
                          >
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Commitment kérdés eltávolítva (2026-06) */}

                <div className="flex gap-4 pt-2">
                  <Button
                    data-testid="booking-back-btn"
                    type="button"
                    variant="outline"
                    onClick={() => setBookingStep('select')}
                    className="flex-1"
                  >
                    Vissza
                  </Button>
                  <Button
                    data-testid="booking-confirm-btn"
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold"
                  >
                    {isSubmitting ? 'Foglalás…' : 'Időpont lefoglalása'}
                  </Button>
                </div>

                {/* Felmérési politika — csak a 60 perces időpontnál releváns */}
                {duration === 60 && (
                <div
                  data-testid="booking-assessment-policy"
                  className={`mt-3 p-4 rounded-lg text-sm leading-relaxed ${
                    paidAssessment
                      ? 'bg-gray-50 border border-gray-200 text-gray-700'
                      : 'bg-emerald-600 text-white shadow-sm'
                  }`}
                >
                  {paidAssessment ? (
                    <p>
                      <span className="font-semibold text-gray-900">Felmérési politika: </span>
                      Az állapotfelmérés és konzultáció (0. alkalom) bérletvásárlás esetén{' '}
                      <span className="font-semibold text-yellow-700">INGYENES</span>. Bérletvásárlás hiányában
                      a felmérés díja <span className="font-semibold">10.000 Ft</span>, amely a helyszínen fizetendő.
                    </p>
                  ) : (
                    <p>
                      <span className="font-extrabold uppercase tracking-wide">Ingyenes állapotfelmérés: </span>
                      A 60 perces konzultáció és a részletes állapotfelmérés (0. alkalom){' '}
                      <span className="font-extrabold">teljesen díjmentes</span> — bérletvásárlás nélkül is,
                      semmilyen díjat nem kell fizetned.
                    </p>
                  )}
                </div>
                )}
              </form>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
